/**
 * SongQueueState — YouTube song play queue with a hard cap.
 *
 * Policy (Alex 2026-04-08): Guide may recommend songs at any rate
 * (including during standstill). The frontend enforces pace via this
 * queue. The currently-playing song always finishes naturally; newly
 * arrived recommendations wait in the queue. If the queue exceeds
 * MAX_QUEUE_SIZE we drop the OLDEST pending item — the newest
 * recommendations reflect the user's most recent GPS context, so
 * stale queued items are less valuable.
 *
 * Emits `songs:changed` on any mutation.
 */
export class SongQueueState {
    constructor(_bus) {
        this._bus = _bus;
        this._current = null;
        this._queue = [];
    }
    /** Set the currently playing song (when YT player actually loaded it) */
    setCurrent(song) {
        this._current = song;
        this._emit();
    }
    /**
     * Append to the end of the queue. If the queue is already at MAX_QUEUE_SIZE,
     * the oldest pending item is dropped first (and returned so the caller
     * can log it for debug visibility).
     */
    enqueue(song) {
        let dropped = null;
        while (this._queue.length >= SongQueueState.MAX_QUEUE_SIZE) {
            dropped = this._queue.shift() ?? null;
        }
        this._queue.push(song);
        this._emit();
        return { dropped };
    }
    /** Pop the next song from the queue (caller is responsible for loading it) */
    dequeue() {
        const next = this._queue.shift() ?? null;
        this._emit();
        return next;
    }
    /** Clear all queued songs (current stays playing) */
    clearQueue() {
        this._queue = [];
        this._emit();
    }
    get current() {
        return this._current;
    }
    get hasCurrent() {
        return this._current !== null;
    }
    get queueLength() {
        return this._queue.length;
    }
    get queue() {
        return this._queue;
    }
    /** Look at the next song without removing it */
    peek() {
        return this._queue.length > 0 ? this._queue[0] : null;
    }
    _emit() {
        this._bus.emit('songs:changed', {
            current: this._current,
            queue: this._queue.slice(),
        });
    }
}
/** Max pending songs (not counting the currently playing one) */
SongQueueState.MAX_QUEUE_SIZE = 5;
//# sourceMappingURL=SongQueueState.js.map